import React from 'react';
import Header from '../components/Header';
import Footer from '../components/Footer';
import { Button } from '../components/ui/button';
import { 
  FileText, 
  DollarSign, 
  RefreshCcw, 
  CheckCircle, 
  ArrowRight, 
  LayoutList, 
  Calculator, 
  Database,
  TrendingUp,
  Files,
  Clock
} from 'lucide-react';
import { Link } from 'react-router-dom';

const AlishaEOBPostingPage = () => {
  return (
    <div className="min-h-screen bg-white">
      <Header />
      
      {/* Hero Section */}
      <section className="pt-32 pb-20 px-6 relative overflow-hidden">
        {/* Gradient Background */}
        <div className="absolute inset-0 bg-gradient-to-b from-blue-50 via-indigo-50/50 to-blue-100/70"></div>
        
        <div className="container mx-auto max-w-7xl relative z-10">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
            {/* Left: Content */}
            <div className="space-y-6">
              <div className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-white/80 backdrop-blur-sm text-blue-700 text-xs font-medium border border-blue-200/50">
                <div className="w-1.5 h-1.5 bg-blue-600 rounded-full"></div>
                <span>Meet Alisha, EOB Posting Agent</span>
              </div>
              
              <h1 className="text-5xl md:text-6xl font-bold mb-6 leading-tight">
                Meet Alisha, Your
                <span className="bg-gradient-to-r from-blue-900 via-blue-600 to-blue-400 bg-clip-text text-transparent"> AI EOB Posting Agent</span>
              </h1>
              
              <p className="text-lg text-gray-600 leading-relaxed max-w-lg">
                Alisha ingests EOBs from scanned documents or ERAs, calculates adjustments and patient portions, and posts line-item details directly into the Asha PMS ledger.
              </p>
              
              <div className="flex flex-wrap gap-3 pt-2">
                <Link to="/book-demo">
                  <Button className="bg-blue-600 hover:bg-blue-700 text-white px-8 py-6 rounded-xl text-lg transition-all duration-300 hover:scale-105 shadow-lg shadow-blue-200">
                    Get a Demo
                  </Button>
                </Link>
                <Button variant="outline" className="px-8 py-6 rounded-xl text-lg border-2 hover:bg-gray-50">
                  See Pricing
                </Button>
              </div>
            </div>
            
            {/* Right: Visual */}
            <div className="relative h-[600px] w-full flex items-center justify-center">
                <div className="relative w-full max-w-lg">
                    {/* Main Ledger Card */}
                    <div className="bg-white rounded-[2rem] shadow-2xl p-6 border border-slate-100 relative z-10">
                        {/* Header */}
                        <div className="flex items-center justify-between mb-6 pb-4 border-b border-slate-100">
                             <div className="flex items-center gap-3">
                                 <div className="w-12 h-12 bg-indigo-100 rounded-xl flex items-center justify-center">
                                     <Files className="w-6 h-6 text-indigo-600" />
                                 </div>
                                 <div>
                                     <div className="font-bold text-slate-900">Payment Batch #2991</div>
                                     <div className="text-xs text-slate-500">MetLife • Check #88219</div>
                                 </div>
                             </div>
                             <div className="text-right">
                                 <div className="text-xl font-bold text-green-600">+$4,250.00</div>
                                 <div className="text-xs text-slate-400">Total Posted</div>
                             </div>
                        </div>

                        {/* Line Items */}
                        <div className="space-y-3 mb-6">
                            {[1, 2, 3].map((item) => (
                                <div key={item} className="flex items-center justify-between p-3 bg-slate-50 rounded-xl border border-slate-100">
                                    <div className="flex items-center gap-3">
                                        <div className="w-8 h-8 rounded-full bg-white border border-slate-200 flex items-center justify-center text-xs font-bold text-slate-600">
                                            {item}
                                        </div>
                                        <div>
                                            <div className="text-sm font-bold text-slate-800">Sarah J. • D2740</div>
                                            <div className="text-xs text-slate-500">Allowed: $850.00</div>
                                        </div>
                                    </div>
                                    <div className="flex items-center gap-2">
                                        <div className="text-right">
                                            <div className="text-sm font-bold text-slate-900">$425.00</div>
                                            <div className="text-[10px] text-red-500 font-medium">Adj: -$50.00</div>
                                        </div>
                                        <CheckCircle className="w-4 h-4 text-green-500" />
                                    </div>
                                </div>
                            ))}
                        </div>

                        {/* Reconciliation Status */}
                        <div className="bg-green-50 border border-green-100 rounded-xl p-4 flex items-center justify-between">
                            <div className="flex items-center gap-2">
                                <CheckCircle className="w-5 h-5 text-green-600" />
                                <span className="font-bold text-green-900 text-sm">Balanced to Penny</span>
                            </div>
                            <span className="text-xs font-mono text-green-700 bg-white px-2 py-1 rounded border border-green-200">
                                $0.00 Variance
                            </span>
                        </div>
                    </div>

                    {/* Floating ROI */}
                    <div className="absolute -left-6 top-24 bg-white p-4 rounded-xl shadow-xl border border-slate-100 animate-float">
                        <div className="flex items-center gap-3">
                            <div className="w-10 h-10 bg-blue-100 rounded-full flex items-center justify-center">
                                <Clock className="w-5 h-5 text-blue-600" />
                            </div>
                            <div>
                                <div className="text-xs text-slate-500">Processing Time</div>
                                <div className="text-lg font-bold text-slate-900">0.4s / claim</div>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
          </div>
        </div>
      </section>

      {/* Feature Cards Section */}
      <section className="py-24 px-6 bg-white">
        <div className="container mx-auto max-w-7xl">
          <div className="text-center mb-16 max-w-3xl mx-auto">
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-6">
              Precision Accounting
            </h2>
            <p className="text-lg text-gray-600">
              Eliminate manual data entry errors and ensure your accounts match the bank deposit to the penny.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            {/* Card 1 */}
            <div className="bg-slate-50 rounded-2xl p-8 border border-slate-100 hover:shadow-lg transition-all group">
              <div className="w-12 h-12 bg-white rounded-xl shadow-sm flex items-center justify-center mb-6 text-blue-600 group-hover:scale-110 transition-transform">
                <LayoutList className="w-6 h-6" />
              </div>
              <h3 className="text-xl font-bold text-gray-900 mb-3">Line-Item Posting</h3>
              <p className="text-gray-600">
                Alisha doesn't just post the total. She breaks down payments by procedure code (CDT), attaching the correct write-off to each line.
              </p>
            </div>

            {/* Card 2 */}
            <div className="bg-slate-50 rounded-2xl p-8 border border-slate-100 hover:shadow-lg transition-all group">
              <div className="w-12 h-12 bg-white rounded-xl shadow-sm flex items-center justify-center mb-6 text-purple-600 group-hover:scale-110 transition-transform">
                <Calculator className="w-6 h-6" />
              </div>
              <h3 className="text-xl font-bold text-gray-900 mb-3">Math Verification</h3>
              <p className="text-gray-600">
                She verifies that the insurance payment + write-off + patient copay equals the total fee. If it doesn't, she flags it.
              </p>
            </div>

            {/* Card 3 */}
            <div className="bg-slate-50 rounded-2xl p-8 border border-slate-100 hover:shadow-lg transition-all group">
              <div className="w-12 h-12 bg-white rounded-xl shadow-sm flex items-center justify-center mb-6 text-green-600 group-hover:scale-110 transition-transform">
                <Database className="w-6 h-6" />
              </div>
              <h3 className="text-xl font-bold text-gray-900 mb-3">Universal Ingestion</h3>
              <p className="text-gray-600">
                Paper checks, scanned PDFs, or 835 ERAs—Alisha handles them all and standardizes the data for your system.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Workflow Visualization */}
      <section className="py-24 px-6 bg-slate-900 text-white relative overflow-hidden">
        <div className="absolute top-0 right-0 w-[600px] h-[600px] bg-blue-600/20 rounded-full blur-[100px] -translate-y-1/2 translate-x-1/2"></div>
        <div className="absolute bottom-0 left-0 w-[600px] h-[600px] bg-green-600/20 rounded-full blur-[100px] translate-y-1/2 -translate-x-1/2"></div>

        <div className="container mx-auto max-w-7xl relative z-10">
          <div className="grid md:grid-cols-2 gap-16 items-center">
            <div>
              <div className="inline-flex items-center gap-2 text-blue-400 font-bold tracking-wider text-xs uppercase mb-4">
                <RefreshCcw className="w-4 h-4" />
                The Posting Workflow
              </div>
              <h2 className="text-3xl md:text-5xl font-bold mb-6 leading-tight">
                How Alisha Works
              </h2>
              <p className="text-slate-400 text-lg leading-relaxed mb-8">
                From mailroom to bank deposit, Alisha automates the revenue cycle's final mile.
              </p>
              
              <div className="space-y-6">
                <div className="flex gap-4">
                  <div className="w-8 h-8 rounded-full bg-blue-600 flex items-center justify-center flex-shrink-0 font-bold">1</div>
                  <div>
                    <h4 className="text-xl font-bold mb-1">Scans Documents</h4>
                    <p className="text-slate-400 text-sm">Uses OCR to read paper EOBs or parses digital ERA files.</p>
                  </div>
                </div>
                <div className="flex gap-4">
                  <div className="w-8 h-8 rounded-full bg-indigo-600 flex items-center justify-center flex-shrink-0 font-bold">2</div>
                  <div>
                    <h4 className="text-xl font-bold mb-1">Matches Patients</h4>
                    <p className="text-slate-400 text-sm">Links the payment to the correct patient and appointment date.</p>
                  </div>
                </div>
                <div className="flex gap-4">
                  <div className="w-8 h-8 rounded-full bg-purple-600 flex items-center justify-center flex-shrink-0 font-bold">3</div>
                  <div>
                    <h4 className="text-xl font-bold mb-1">Calculates Splits</h4>
                    <p className="text-slate-400 text-sm">Separates insurance payment from patient responsibility.</p>
                  </div>
                </div>
                <div className="flex gap-4">
                  <div className="w-8 h-8 rounded-full bg-green-600 flex items-center justify-center flex-shrink-0 font-bold">4</div>
                  <div>
                    <h4 className="text-xl font-bold mb-1">Posts to Ledger</h4>
                    <p className="text-slate-400 text-sm">Writes the transaction directly into the patient's financial history.</p>
                  </div>
                </div>
              </div>
            </div>

            <div className="relative">
               <div className="bg-slate-800/50 backdrop-blur-xl rounded-3xl p-8 border border-slate-700 shadow-2xl">
                  <div className="flex items-center justify-between mb-8">
                      <div className="flex items-center gap-3">
                          <div className="w-10 h-10 rounded-full bg-green-500/20 flex items-center justify-center">
                              <DollarSign className="w-5 h-5 text-green-400" />
                          </div>
                          <div>
                              <div className="font-bold text-white">Payment Posted</div>
                              <div className="text-xs text-slate-400">Batch #9921</div>
                          </div>
                      </div>
                      <div className="text-xs font-mono text-green-400 font-bold">+$1,240.00</div>
                  </div>

                  <div className="space-y-3">
                      <div className="bg-slate-700/50 p-3 rounded-lg border border-slate-600 flex justify-between items-center">
                          <div>
                              <div className="text-xs text-slate-400">Patient</div>
                              <div className="text-sm font-bold text-slate-200">Sarah Jenkins</div>
                          </div>
                          <div className="text-right">
                              <div className="text-xs text-slate-400">Ins Paid</div>
                              <div className="text-sm font-bold text-green-400">$850.00</div>
                          </div>
                      </div>
                      <div className="bg-slate-700/50 p-3 rounded-lg border border-slate-600 flex justify-between items-center">
                          <div>
                              <div className="text-xs text-slate-400">Patient</div>
                              <div className="text-sm font-bold text-slate-200">Mike Ross</div>
                          </div>
                          <div className="text-right">
                              <div className="text-xs text-slate-400">Ins Paid</div>
                              <div className="text-sm font-bold text-green-400">$390.00</div>
                          </div>
                      </div>
                  </div>
                  
                  <div className="mt-6 pt-4 border-t border-slate-700 flex justify-between items-center">
                      <span className="text-xs text-slate-400">Status</span>
                      <span className="bg-green-500/10 text-green-400 px-3 py-1 rounded-full text-xs font-bold border border-green-500/20">
                          Reconciled
                      </span>
                  </div>
               </div>
            </div>
          </div>
        </div>
      </section>

      {/* ROI Stats */}
      <section className="py-20 px-6 bg-white">
        <div className="container mx-auto max-w-6xl">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            <div className="bg-blue-50 p-8 rounded-3xl text-center">
              <div className="text-4xl font-bold text-blue-600 mb-2">90%</div>
              <div className="text-gray-600 font-medium">Reduction in Manual Entry</div>
            </div>
            <div className="bg-indigo-50 p-8 rounded-3xl text-center">
              <div className="text-4xl font-bold text-indigo-600 mb-2">24h</div>
              <div className="text-gray-600 font-medium">Faster Payment Cycle</div>
            </div>
            <div className="bg-purple-50 p-8 rounded-3xl text-center">
              <div className="text-4xl font-bold text-purple-600 mb-2">100%</div>
              <div className="text-gray-600 font-medium">Ledger Accuracy</div>
            </div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 px-6">
        <div className="container mx-auto max-w-4xl text-center">
          <h2 className="text-4xl font-bold text-gray-900 mb-6">
            Get paid faster.
          </h2>
          <p className="text-xl text-gray-600 mb-10 max-w-2xl mx-auto">
            Let Alisha automate your posting so your books are always up to date.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Link to="/book-demo">
              <Button className="bg-blue-600 hover:bg-blue-700 text-white px-10 py-6 rounded-full text-lg shadow-xl shadow-blue-200 hover:shadow-blue-300 hover:-translate-y-1 transition-all">
                Automate Posting
              </Button>
            </Link>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
};

export default AlishaEOBPostingPage;
